import type { CreationTask } from '../api/creations'
import type {
  GenItem,
  TimelinePendingItem,
  TimelineRound,
} from '../types/timeline'
import {
  parseOutputSuperResolutionFromCreationMetadata,
  sliceOutputSuperResolutionForImageCount,
} from './outputSuperResolutionMeta'
import { randomUuid } from './randomUuid'
import { sanitizeUserFacingErrorMessage } from './userFacingErrorMessage'

function normalizeTaskError(task: CreationTask): string {
  const raw = task.errorMessage?.trim()
  if (!raw) return '生成失败，请稍后重试'
  const sanitized = sanitizeUserFacingErrorMessage(raw)
  return sanitized.trim() || '生成失败，请稍后重试'
}

function resolveStoragePublicIds(task: CreationTask): string[] {
  const fromResult = task.result?.storagePublicIds
  if (Array.isArray(fromResult) && fromResult.length > 0) {
    return fromResult.map((id) => id.trim()).filter(Boolean)
  }
  const meta = task.result?.metadata?.storagePublicIds
  if (Array.isArray(meta)) {
    return meta.map((id) => String(id).trim()).filter(Boolean)
  }
  return []
}

/**
 * 移除已完成任务的骨架占位。
 *
 * 追加生成时骨架挂在原轮次上，任务终态回写后必须清掉，
 * 否则时间线会长期留一个永不消失的占位卡。
 *
 * @param pendingItems 原轮次骨架列表
 * @param creationTaskId 终态任务号
 * @return 过滤后的骨架列表（无骨架时返回 []）
 */
function dropTaskSkeleton(
  pendingItems: TimelineRound['pendingItems'],
  creationTaskId: string,
): TimelinePendingItem[] {
  return (pendingItems ?? []).filter((s) => s.creationTaskId !== creationTaskId)
}

/**
 * 将轮询得到的创作任务终态合并回时间线轮次；未完成则返回 null。
 * 成功轮次要求每张图同时有展示地址与存储公开编号。
 *
 * @param isAppend 追加生成场景：新图追加到原 items 末尾（不替换已有图），
 *                 并移除 creationTaskId 匹配的骨架；非追加场景全量替换 items。
 */
export function mergeTerminalCreationIntoRound(
  round: TimelineRound,
  task: CreationTask,
  imageCount = 1,
  isAppend = false,
): TimelineRound | null {
  if (task.status === 'SUCCEEDED' && task.result?.imageUrls?.length) {
    const imageUrls = task.result.imageUrls.slice(0, imageCount)
    const storagePublicIds = resolveStoragePublicIds(task).slice(0, imageCount)
    if (
      storagePublicIds.length !== imageUrls.length ||
      imageUrls.some((u) => !u?.trim()) ||
      storagePublicIds.some((id) => !id)
    ) {
      return {
        ...round,
        taskId: task.publicId,
        status: 'UNAVAILABLE',
        errorMessage: '生成完成但存储登记不完整',
        // 追加场景原图必须保留：该失败只影响本次追加，不能清空整轮
        items: isAppend ? round.items : [],
        pendingItems: dropTaskSkeleton(round.pendingItems, task.publicId),
      }
    }
    const sr = sliceOutputSuperResolutionForImageCount(
      parseOutputSuperResolutionFromCreationMetadata(task.result.metadata),
      imageCount,
    )
    const nextItems: GenItem[] = imageUrls.map((src, index) => ({
      id: randomUuid(),
      roundId: round.id,
      prompt: round.prompt,
      src: src.trim(),
      storagePublicId: storagePublicIds[index]!,
      at: round.at,
      modelId: round.modelId,
      modelLabel: round.modelLabel,
      aspectLabel: round.aspectLabel,
    }))
    return {
      ...round,
      taskId: task.publicId,
      status: 'SUCCEEDED',
      errorMessage: undefined,
      // 追加只在原 items 末尾接新图；首次生图则是全量替换
      items: isAppend ? [...round.items, ...nextItems] : nextItems,
      pendingItems: dropTaskSkeleton(round.pendingItems, task.publicId),
      ...(sr ? { outputSuperResolution: sr } : {}),
    }
  }
  if (task.status === 'SUCCEEDED') {
    return {
      ...round,
      taskId: task.publicId,
      status: 'UNAVAILABLE',
      errorMessage: undefined,
      items: isAppend ? round.items : [],
      pendingItems: dropTaskSkeleton(round.pendingItems, task.publicId),
    }
  }
  if (task.status === 'FAILED') {
    return {
      ...round,
      taskId: task.publicId,
      // 追加生成失败只丢骨架、保留原成功态：绝不能把整轮翻成 FAILED（原图已成功）
      status: isAppend ? (round.status ?? 'SUCCEEDED') : 'FAILED',
      errorMessage: isAppend ? round.errorMessage : normalizeTaskError(task),
      errorCode: isAppend ? round.errorCode : (task.errorCode ?? undefined),
      reconcilable: isAppend ? round.reconcilable : task.reconcilable,
      items: isAppend ? round.items : [],
      pendingItems: dropTaskSkeleton(round.pendingItems, task.publicId),
    }
  }
  return null
}
